import { Location } from "history";
import { useLocation } from "react-router-dom";

import { VocabularyWordSummary } from "@seneca/course-service-types";

import { getAssignmentWordBestScore } from "seneca-common/features/assignments/features/student-assignments/state/selectors";
import { useSelectorWithArgs } from "seneca-common/utils/hooks/selectors";

import Percentage from "features/classroom/common/Percentage";
import useCourseId from "features/classroom/routes/classroom/routes/course/hooks/useCourseId";
import { ROUTES } from "routing/routes";

import { RowLayoutLinkConditional } from "../../../common";
import RowNumber from "../../../common/components/RowNumber";
import RowTitle from "../../../common/components/RowTitle";
import useWordRowQuitSessionLink from "./hooks/useWordRowQuitSessionLink";

type Props = {
  number: number;
  assignmentId: string;
  word: VocabularyWordSummary;
  activeWordId: string;
};

export default function AssignmentWordRow({
  number,
  assignmentId,
  word: { word, wordId },
  activeWordId
}: Props) {
  const bestScore = useSelectorWithArgs(
    getAssignmentWordBestScore,
    assignmentId,
    wordId
  );

  const courseId = useCourseId();

  const isRowActive = activeWordId === wordId;

  const location = useLocation();

  const linkToStartVocabSession = {
    pathname: ROUTES.CLASSROOM.COURSE.VOCABULARY.SESSION.START.buildPath({
      courseId: courseId!,
      wordId
    }),
    state: ROUTES.CLASSROOM.COURSE.VOCABULARY.SESSION.START.buildState({
      assignmentId,
      locationFrom: location.pathname
    })
  } as Location;

  const link = useWordRowQuitSessionLink({
    courseId,
    link: linkToStartVocabSession
  });

  return (
    <RowLayoutLinkConditional link={link} active={isRowActive}>
      {{
        title: (
          <>
            <RowNumber>{number}.</RowNumber>
            <RowTitle title={word} dataTestName="AssignmentWord" />
          </>
        ),
        indicator: <Percentage value={bestScore} />
      }}
    </RowLayoutLinkConditional>
  );
}
